import axiosInstance from "@/plugins/axios.ts"

/** A still image runs for one standard slot length (mirrors the backend). */
export const IMAGE_DURATION_MS = 15_000

/**
 * Read a picked file's duration in ms WITHOUT uploading — images return the
 * fixed 15s slot length, videos read <video>.duration from metadata. This is a
 * client-side HINT (browser duration drifts from ffprobe by ~1 frame); the
 * backend ffprobe stays the source of truth, so callers compare with tolerance.
 */
export const readMediaDuration = (file: File): Promise<number> =>
	new Promise((resolve) => {
		if (!file.type.startsWith("video/")) {
			resolve(IMAGE_DURATION_MS)
			return
		}
		const v = document.createElement("video")
		v.preload = "metadata"
		const url = URL.createObjectURL(file)
		v.onloadedmetadata = () => {
			URL.revokeObjectURL(url)
			const ms = Math.round((v.duration || 0) * 1000)
			resolve(ms > 0 ? ms : 0)
		}
		v.onerror = () => {
			URL.revokeObjectURL(url)
			resolve(0)
		}
		v.src = url
	})

const createImageElement = (document: Document, file_id: number): HTMLImageElement => {
	let image = new Image()
	image.hidden = true
	document.body.appendChild(image)
	image.id = `image_id_${file_id}`
	return image
}
const createVideoElement = (document: Document, file_id: number): HTMLVideoElement => {
	let video = document.createElement("video") as HTMLVideoElement
	video.hidden = true
	document.body.appendChild(video)
	video.id = `video_id_${file_id}`
	return video
}
const createCanvasElement = (document: Document, file_id: number): HTMLCanvasElement => {
	let canvas = document.createElement("canvas") as HTMLCanvasElement
	canvas.hidden = true
	document.body.appendChild(canvas)
	canvas.id = `canvas_id_${file_id}`
	return canvas
}

export const getMediaThumbnail = async (document: Document, file: File, file_id: number): Promise<{
	status: boolean,
	url: string,
	width: number,
	height: number
}> => {
	let fileReader = new FileReader()

	var thumbUrl: { status: boolean, url: string, width: number, height: number, } | string | null = null

	if (file.type.match("image")) {

		fileReader.readAsDataURL(file)
		return new Promise((resolve, reject) => {

			fileReader.onload = async () => {
				let img = createImageElement(document, file_id)
				document.body.appendChild(img)
				img.id = `image_id_${file_id}`

				img.onload = () => {
					thumbUrl = { status: true, url: img.src, width: img.width, height: img.height }
					resolve(thumbUrl)
					img.remove()
				}

				img.src = String(fileReader.result)
			}
		})

	} else {

		fileReader.readAsArrayBuffer(file)

		return new Promise((resolve, reject) => {

			fileReader.onload = async function() {

				let blob = new Blob([fileReader.result!], { type: file.type })
				let url = URL.createObjectURL(blob)
				let video = createVideoElement(document, file_id)


				let snapImage = async function() {
					let canvas = document.getElementById(`canvas_id_${file_id}`) as HTMLCanvasElement

					if (!canvas) canvas = createCanvasElement(document, file_id)
					canvas.width = video.videoWidth
					canvas.height = video.videoHeight
					canvas.getContext("2d")?.drawImage(video, 0, 0, canvas.width, canvas.height)

					let image = canvas.toDataURL()
					let success = image.length > 100000

					if (success) {

						video.pause()
						video.removeEventListener("timeupdate", timeupdate)
						URL.revokeObjectURL(url)

					}


					return { status: success, url: image, width: video.videoWidth, height: video.videoHeight }


				}

				let timeupdate = async () => {

					let snap = await snapImage()
					// thumbUrl = JSON.stringify(snap)


					if (snap.status) {
						resolve(snap)

						video.pause()
						video.removeEventListener("timeupdate", timeupdate)
						video.removeEventListener("loadeddata", timeupdate)

						document.getElementById(`canvas_id_${file_id}`)?.remove()
						document.getElementById(`video_id_${file_id}`)?.remove()
						document.getElementById(`image_id_${file_id}`)?.remove()
					}

				}

				video.addEventListener("loadeddata", async () => {

					let snap = await snapImage()
					thumbUrl = JSON.stringify(snap)

					if (snap.status) {

						resolve(snap)

						video.removeEventListener("timeupdate", timeupdate)
						video.removeEventListener("loadeddata", timeupdate)

						document.getElementById(`canvas_id_${file_id}`)?.remove()
						document.getElementById(`video_id_${file_id}`)?.remove()
						document.getElementById(`image_id_${file_id}`)?.remove()
					}

				})


				video.addEventListener("timeupdate", timeupdate)


				video.preload = "metadata"
				video.src = url

				// Load video in Safari / IE11
				video.muted = true
				video.playsInline = true
				await video.play()
			}


		})

	}
}

export const getMediaSrc = async (url: string): Promise<string> => {
	let mediaSrc = ""
	await axiosInstance.get(url, {
		responseType: "blob",
		headers: {
			"accept": "application/json, video/*, image/*",
		}
	}).then((response) => {
		const URL = window.URL || window.webkitURL
		mediaSrc = URL.createObjectURL(response.data)
	})
	return mediaSrc

}